import React, { useEffect, useState } from 'react';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
    selected?: boolean;
}

export interface SelectInputProps {
    label?: string;
    options: SelectOption[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    labelClassName?: string;
    selectClassName?: string;
    id?: string;
    errorMessage?: string;
}

const SelectInput = ({
    label,
    options,
    value,
    onChange,
    placeholder = '선택해주세요',
    disabled = false,
    required = false,
    className = '',
    labelClassName = 'form-label fs-14 text-dark mb-1',
    selectClassName = 'form-select cursor-pointer',
    id,
    errorMessage,
}: SelectInputProps) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const [error, setError] = useState<string>('');
    const [touched, setTouched] = useState<boolean>(false);

    const validate = (val: string): string => {
        // required 체크
        if (required && (!val || val === '')) {
            return errorMessage || `${label || '항목'}을(를) 선택해주세요.`;
        }

        return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value;

        if (onChange) {
            onChange(newValue);
        }

        if (touched) {
            const validationError = validate(newValue);
            setError(validationError);
        }
    };

    const handleBlur = () => {
        setTouched(true);
        const validationError = validate(value || '');
        setError(validationError);
    };

    useEffect(() => {
        if (touched) {
            const validationError = validate(value || '');
            setError(validationError);
        }
    }, [value, touched]);

    // 외부에서 유효성 검사 결과를 확인할 수 있도록
    const isValid = !validate(value || '');

    return (
        <div className={className}>
            {label && (
                <label htmlFor={selectId} className={labelClassName}>
                    {label}
                    {required && <span className="text-danger">*</span>}
                </label>
            )}
            <select
                id={selectId}
                className={`${selectClassName} ${error ? 'is-invalid' : ''}`}
                value={value || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                required={required}
                aria-label={label || 'Select option'}
                data-valid={isValid}
                data-required={required}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {options.map(option => (
                    <option
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        {...(option.selected && { selected: true })}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            {error && touched && (
                <div className="invalid-feedback d-block">{error}</div>
            )}
        </div>
    );
};

export default SelectInput;
